import { useState, useRef } from "react";
import { Play, Share2, Clock, Pencil, Trash2, ChevronDown, ChevronUp, Download, LayoutDashboard, Globe2, Lock, UserMinus, Users } from "lucide-react";
import { userReportsApi } from "../../api";
import { useAuth } from "../../hooks/useAuth";
import { downloadCsv, downloadJson, slugify } from "../../utils";
import { useConfirm, useClickOutside, useReportPreview, useReportSubscription } from "../../hooks";
import { DataTable } from "../ui";
import ReportFormModal from "./ReportFormModal";
import ShareReportModal from "./ShareReportModal";
import ScheduleModal from "./ScheduleModal";
import PinModal from "../Dashboard/PinModal";
import type { UserReport } from "../../types/reports";
import ReportBadge from "./ReportBadge";

interface ReportCardProps {
  report: UserReport;
  onRefresh: () => void;
}

export default function ReportCard({ report, onRefresh }: ReportCardProps) {
  const { user } = useAuth();
  const { confirm, confirmDialog } = useConfirm();
  const isOwner = report.owner?.id === user?.id;
  const { preview, previewLoading, previewOpen, togglePreview } = useReportPreview(report.id);
  const {
    subscriptionLoading: unsubscribeLoading,
    subscriptionError: actionError,
    unsubscribe,
  } = useReportSubscription({
    reportId: report.id,
    isSubscribed: report.is_subscribed ?? false,
    onChange: onRefresh,
  });

  const [showEdit, setShowEdit] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [showPin, setShowPin] = useState(false);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const downloadRef = useRef<HTMLDivElement>(null);

  useClickOutside(downloadRef, () => setDownloadOpen(false), downloadOpen);

  async function handleDownload(format: "csv" | "json") {
    setDownloadOpen(false);
    setDownloadLoading(true);
    try {
      const res = await userReportsApi.preview(report.id);
      const rows = res.data?.rows || [];
      const filename = slugify(report.name);
      if (format === "csv") downloadCsv(rows, filename);
      else downloadJson(rows, filename);
    } catch {
      // preview endpoint will surface errors to user if needed
    } finally {
      setDownloadLoading(false);
    }
  }

  async function deleteReport() {
    const ok = await confirm({
      title: "Delete report",
      message: `Delete "${report.name}"? This can't be undone.`,
      confirmLabel: "Delete",
      danger: true,
    });
    if (!ok) return;
    await userReportsApi.delete(report.id);
    onRefresh();
  }

  return (
    <article
      id={`report-${report.id}`}
      tabIndex={-1}
      className="rounded-xl border border-gray-200 bg-white p-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-gray-800">{report.name}</h3>
          <div className="mt-2 flex flex-wrap items-center gap-1.5" aria-label="Report details">
            {!isOwner && report.is_subscribed && (
              <ReportBadge tone="purple">
                Subscribed · by {report.owner?.full_name}
              </ReportBadge>
            )}
            {!isOwner && !report.is_subscribed && (
              <ReportBadge tone="purple">
                Shared by {report.owner?.full_name}
              </ReportBadge>
            )}
            {isOwner && (
              <ReportBadge tone={report.is_public ? "blue" : "neutral"}>
                {report.is_public ? <Globe2 size={11} aria-hidden="true" /> : <Lock size={11} aria-hidden="true" />}
                {report.is_public ? "Public" : "Private"}
              </ReportBadge>
            )}
            {isOwner && report.is_public && (
              <ReportBadge>
                <Users size={11} aria-hidden="true" />
                {report.subscriber_count ?? 0} {(report.subscriber_count ?? 0) === 1 ? "subscriber" : "subscribers"}
              </ReportBadge>
            )}
            {(report.shares?.length ?? 0) > 0 && isOwner && (
              <ReportBadge tone="blue">
                Shared with {report.shares!.length}
              </ReportBadge>
            )}
            {report.pinned_to_dashboard && (
              <ReportBadge tone="indigo">
                <LayoutDashboard size={11} aria-hidden="true" />
                On dashboard
              </ReportBadge>
            )}
            {report.schedule?.is_active && (
              <ReportBadge tone="green">
                <Clock size={11} aria-hidden="true" />
                {report.schedule.label}
              </ReportBadge>
            )}
          </div>
          {report.description && (
            <p className="mt-2 text-sm text-gray-500">{report.description}</p>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-1 sm:shrink-0 sm:flex-nowrap">
          {(report.sql_query || report.metric_id) && (
            <>
              <button onClick={togglePreview} disabled={previewLoading}
                title="Preview"
                className="flex items-center gap-1 rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-40">
                <Play size={11} />
                {previewLoading ? "Loading..." : previewOpen ? "Hide" : "Preview"}
                {previewOpen ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
              </button>

              <div className="relative" ref={downloadRef}>
                <button
                  onClick={() => setDownloadOpen((o) => !o)}
                  disabled={downloadLoading}
                  title="Download"
                  className="flex items-center gap-1 rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-40"
                >
                  <Download size={11} />
                  {downloadLoading ? "..." : "Download"}
                  <ChevronDown size={10} />
                </button>
                {downloadOpen && (
                  <div className="absolute right-0 top-full z-10 mt-1 w-28 rounded-lg border border-gray-200 bg-white shadow-md">
                    <button
                      onClick={() => handleDownload("csv")}
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-gray-700 hover:bg-gray-50 rounded-t-lg"
                    >
                      CSV
                    </button>
                    <button
                      onClick={() => handleDownload("json")}
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-gray-700 hover:bg-gray-50 rounded-b-lg"
                    >
                      JSON
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
          {(report.sql_query || report.metric_id) && !report.is_subscribed && (
            <button
              onClick={() => setShowPin(true)}
              title={report.pinned_to_dashboard ? "Edit dashboard tile" : "Pin to Dashboard"}
              className={`rounded p-1.5 ${report.pinned_to_dashboard ? "text-blue-600 hover:text-blue-700" : "text-gray-400 hover:text-blue-600"}`}
            >
              <LayoutDashboard size={14} />
            </button>
          )}
          {!isOwner && report.is_subscribed && (
            <button
              onClick={unsubscribe}
              disabled={unsubscribeLoading}
              title="Unsubscribe"
              className="flex items-center gap-1 rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-40"
            >
              <UserMinus size={12} aria-hidden="true" />
              {unsubscribeLoading ? "Updating..." : "Unsubscribe"}
            </button>
          )}
          {isOwner && (
            <>
              <button onClick={() => setShowShare(true)} title="Manage access" aria-label="Manage access" className="rounded p-1.5 text-gray-400 hover:text-blue-600"><Share2 size={14} /></button>
              <button onClick={() => setShowSchedule(true)} title="Schedule" className="rounded p-1.5 text-gray-400 hover:text-green-600"><Clock size={14} /></button>
              <button onClick={() => setShowEdit(true)} title="Edit" className="rounded p-1.5 text-gray-400 hover:text-blue-600"><Pencil size={14} /></button>
              <button onClick={deleteReport} title="Delete" className="rounded p-1.5 text-gray-400 hover:text-red-500"><Trash2 size={14} /></button>
            </>
          )}
        </div>
      </div>

      {actionError && <p role="alert" className="mt-3 text-sm text-red-600">{actionError}</p>}

      {previewOpen && preview && (
        <div className="mt-3">
          {preview.error
            ? <p className="text-sm text-red-600">{preview.error}</p>
            : <><p className="mb-1 text-xs text-gray-400">{preview.row_count} rows</p><DataTable rows={preview.rows} /></>
          }
        </div>
      )}

      {showEdit && <ReportFormModal report={report} onClose={() => setShowEdit(false)} onSuccess={() => { setShowEdit(false); onRefresh(); }} />}
      {showShare && <ShareReportModal report={report} onClose={() => setShowShare(false)} onSuccess={onRefresh} />}
      {showSchedule && <ScheduleModal report={report} onClose={() => setShowSchedule(false)} onSuccess={onRefresh} />}
      {showPin && <PinModal report={report} onClose={() => setShowPin(false)} onSuccess={() => { setShowPin(false); onRefresh(); }} />}
      {confirmDialog}
    </article>
  );
}
